import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import {
  ArrowLeft,
  BadgeCheck,
  Boxes,
  Building2,
  Save as SaveIcon,
  User as UserIcon,
  ChevronRight,
  FolderTree,
} from "lucide-react";

const API = "https://supremeadmin.nissi-india.com";

const PRODUCTS = [
  { value: "INVENTORY", label: "Inventory" },
  { value: "PAYROLL", label: "Payroll" },
  { value: "TALLY", label: "Tally" },
];

function useTenantId() {
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tid");
  }, [location.search]);
}

function buildPageTree(pages) {
  const map = new Map();
  const roots = [];

  pages.forEach((p) => {
    map.set(p.PageId, { ...p, children: [] });
  });

  pages.forEach((p) => {
    const node = map.get(p.PageId);
    if (p.ParentPageId && map.has(p.ParentPageId)) {
      map.get(p.ParentPageId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sort = (nodes) => {
    nodes.sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));
    nodes.forEach((n) => sort(n.children));
  };
  sort(roots);
  return roots;
}

function PageCheckbox({ node, checked, onToggle, level = 0, disabled }) {
  const [open, setOpen] = useState(level < 2);
  const hasChildren = node.children?.length > 0;
  // Sub-module if no route, page only if it has a route
  const isPage = !!node.RoutePath;
  const isChecked = !!checked[node.PageId];

  return (
    <div>
      <div
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-emerald-50 ${
          isChecked ? "bg-emerald-50" : ""
        }`}
        style={{ paddingLeft: 12 + level * 20 }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-slate-400 hover:text-emerald-600"
          >
            <ChevronRight
              size={16}
              className={`transition ${open ? "rotate-90" : ""}`}
            />
          </button>
        ) : (
          <span className="w-4" />
        )}

        {isPage ? (
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={isChecked}
            onChange={() => onToggle(node.PageId)}
            disabled={disabled}
          />
        ) : (
          <span className="w-4" />
        )}

        <span
          className={`flex-1 ${
            isPage ? "text-slate-700" : "font-semibold text-slate-800"
          }`}
        >
          {node.PageName}
        </span>

        {isPage && node.RoutePath && (
          <span className="font-mono text-[11px] text-slate-400">
            {node.RoutePath}
          </span>
        )}
      </div>

      {hasChildren && open && (
        <div>
          {node.children.map((child) => (
            <PageCheckbox
              key={child.PageId}
              node={child}
              checked={checked}
              onToggle={onToggle}
              level={level + 1}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin_Module() {
  const { username: routeUsername } = useParams();
  const tenantId = useTenantId();
  const navigate = useNavigate();

  const username = useMemo(() => {
    try {
      return decodeURIComponent(routeUsername || "").trim();
    } catch {
      return String(routeUsername || "").trim();
    }
  }, [routeUsername]);

  const [product, setProduct] = useState("");
  const [modules, setModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [pages, setPages] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);

  const pageTree = useMemo(() => buildPageTree(pages), [pages]);

  const selectedCount = useMemo(
    () => Object.values(checked).filter(Boolean).length,
    [checked]
  );

  // Load modules when product changes
  useEffect(() => {
    if (!product) {
      setModules([]);
      setSelectedModuleId("");
      setPages([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/modules`, {
          params: { product },
          withCredentials: true,
        });
        if (!cancelled) {
          setModules(Array.isArray(res.data?.data) ? res.data.data : []);
        }
      } catch (err) {
        if (!cancelled) setModules([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [product]);

  // Load pages + existing rights when module changes
  useEffect(() => {
    if (!selectedModuleId || !tenantId || !username) {
      setPages([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const [pagesRes, rightsRes] = await Promise.all([
          axios.get(`${API}/api/modules/${selectedModuleId}/pages`, {
            withCredentials: true,
          }),
          axios.get(
            `${API}/api/tenant/modules/${tenantId}/${encodeURIComponent(username)}`,
            { withCredentials: true }
          ),
        ]);

        if (cancelled) return;

        setPages(Array.isArray(pagesRes.data?.data) ? pagesRes.data.data : []);
        setTenant(rightsRes.data?.tenant || null);

        const granted = new Set(
          (rightsRes.data?.pages || [])
            .filter((p) => p.approve)
            .map((p) => p.PageId)
        );

        const next = {};
        (pagesRes.data?.data || []).forEach((p) => {
          if (granted.has(p.PageId)) next[p.PageId] = true;
        });
        setChecked(next);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          Swal.fire("Error", "Unable to load pages or rights", "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedModuleId, tenantId, username]);

  const toggleOne = (pageId) => {
    setChecked((prev) => ({ ...prev, [pageId]: !prev[pageId] }));
  };

 const toggleAll = () => {
  const leafPages = pages.filter((p) => p.RoutePath);
  const allOn = leafPages.every((p) => checked[p.PageId]);
  const next = {};
  if (!allOn) {
    leafPages.forEach((p) => {
      next[p.PageId] = true;
    });
  }
  setChecked(next);
};

  const onSave = async (e) => {
    e.preventDefault();
    if (!tenantId || !username || !selectedModuleId) return;

    const selectedPages = pages
      .filter((p) => checked[p.PageId])
      .map((p) => ({
        PageId: p.PageId,
        PageName: p.PageName,
        RoutePath: p.RoutePath,
        approve: 1,
      }));

    setSaving(true);
    try {
      await axios.put(
        `${API}/api/tenant/modules/${tenantId}/${encodeURIComponent(username)}`,
        { moduleId: Number(selectedModuleId), pages: selectedPages },
        { withCredentials: true }
      );
      await Swal.fire("Saved", "Page rights updated successfully", "success");
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.error || "Unable to save page rights",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex flex-col md:h-screen md:flex-row md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:h-full md:flex-1 md:overflow-auto">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
            <div className="px-4 py-5 sm:px-6 lg:px-8">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                    <Boxes className="h-5 w-5" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-semibold">Assign Page Rights</h1>
                    <p className="text-sm text-white/80">
                      Infra V5 modules and pages
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to="/superAdmin/admin_Menu"
                    className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-1.5 hover:bg-white/20"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Link>
                  <Time />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1">
                  <Building2 className="h-4 w-4" />
                  {tenant?.TenantCode || tenantId || "Tenant"}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1">
                  <UserIcon className="h-4 w-4" /> {username}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1">
                  <BadgeCheck className="h-4 w-4" /> Selected: {selectedCount}
                </span>
              </div>
            </div>
          </div>

          <div className="px-4 py-6 sm:px-6 lg:px-8">
            {/* Product + Module selectors */}
            <div className="mb-5 grid grid-cols-1 gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Product
                </label>
                <select
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"
                >
                  <option value="">-- Select Product --</option>
                  {PRODUCTS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Module
                </label>
                <select
                  value={selectedModuleId}
                  onChange={(e) => setSelectedModuleId(e.target.value)}
                  disabled={!product}
                  className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm disabled:bg-slate-100"
                >
                  <option value="">-- Select Module --</option>
                  {modules.map((m) => (
                    <option key={m.ModuleId} value={m.ModuleId}>
                      {m.ModuleName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <form
              onSubmit={onSave}
              className="rounded-2xl border border-slate-200 bg-white shadow-sm"
            >
              <div className="flex items-center justify-between border-b border-slate-200 bg-emerald-50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <FolderTree className="h-4 w-4 text-emerald-600" />
                  <h3 className="text-sm font-semibold text-slate-800">
                    Page Hierarchy
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={toggleAll}
                  disabled={loading || saving || pages.length === 0}
                  className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-50"
                >
                  {pages.every((p) => checked[p.PageId])
                    ? "Unselect All"
                    : "Select All"}
                </button>
              </div>

              <div className="p-4">
                {!selectedModuleId ? (
                  <div className="py-12 text-center text-slate-400">
                    Select a product and module to assign page rights.
                  </div>
                ) : loading ? (
                  <div className="py-12 text-center text-slate-400">
                    Loading pages...
                  </div>
                ) : pageTree.length === 0 ? (
                  <div className="py-12 text-center text-slate-400">
                    No pages found for this module.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {pageTree.map((node) => (
                      <PageCheckbox
                        key={node.PageId}
                        node={node}
                        checked={checked}
                        onToggle={toggleOne}
                        disabled={saving}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-slate-200 px-4 py-4">
                <div className="text-xs text-slate-500">
                  {selectedCount} page{selectedCount === 1 ? "" : "s"} selected
                </div>
                <button
                  type="submit"
                  disabled={saving || loading || !selectedModuleId}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-white disabled:opacity-50"
                >
                  <SaveIcon className="h-4 w-4" />
                  {saving ? "Saving..." : "Save Rights"}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}